export interface WebSocketMessage {
  type: string;
  text: string;
  timestamp: string;
  sessionId: string;
  metadata?: Record<string, unknown>;
}

export interface ConnectionEstablishedMessage extends WebSocketMessage {
  type: 'connection_established';
  metadata: {
    sessionId: string;
    moduleId: number;
    moduleName: string;
    completionStage: 'chatting' | 'summarizing' | 'reviewing' | 'completed';
  };
}

export interface CompletionSignalMessage extends WebSocketMessage {
  type: 'completion_signal';
  metadata: {
    reason: 'natural_completion' | 'engagement_threshold';
    messageCount: number;
    sessionDuration: number;
    confidence: number;
    analysis?: {
      isComplete: boolean;
      keyInsights: string[];
      nextSteps: string[];
    };
  };
}

export interface AnalysisRequestMessage extends WebSocketMessage {
  type: 'analysis_request';
  metadata: {
    requiredData: string[];
  };
}

export interface ThinkingMessage extends WebSocketMessage {
  type: 'thinking';
  metadata: {
    show: boolean;
  };
}

export interface SummaryDraftMessage extends WebSocketMessage {
  type: 'summary_draft';
  metadata: {
    summaryId: number;
    templateType: string;
    editable: boolean;
    content: SummaryContent;
  };
}

export interface TemplateSection {
  id: string;
  title: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
}

export interface TemplateConfig {
  sections: TemplateSection[];
  introText?: string;
  completionButtonText?: string;
  allowAddSection?: boolean;
  sectionLimits?: {
    minLength: number;
    maxLength: number;
  };
}

export interface SummaryContent {
  template_type: string;
  sections: Record<string, string>;
  template_config?: TemplateConfig;
  metadata: {
    generated_at: string;
    conversation_length: number;
    module_name: string;
  };
}

export interface EngagementMetrics {
  timeSpent: number;
  messageHistory: number;
  scrollEvents: number;
  copyEvents: number;
  inactivityPeriods: number[];
  userSentiment?: string;
}

// REST API Types
export interface ChatSummary {
  id: number;
  userModuleId: number;
  templateType: string;
  content: SummaryContent;
  isFinal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CompletionAnalysis {
  analysis: Record<string, unknown>;
  type: string;
  createdAt: string;
}

export interface CompletionCriteria {
  minMessages: number;
  minDuration: number;
  minEngagementScore: number;
  completionSignals: string[];
  autoDetect: boolean;
}

export interface SummaryConfig {
  templateType: string;
  autoGenerate: boolean;
  requireApproval: boolean;
  showThinkingIndicator: boolean;
  regenerateEnabled: boolean;
}

export interface ChatModuleData {
  welcomeMessage?: string;
  suggestedPrompts?: string[];
  minMessages?: number;
  allowSkip?: boolean;
  completionCriteria?: CompletionCriteria;
  summaryConfig?: SummaryConfig;
}

// Analysis response type for sending to backend
export interface AnalysisResponse {
  type: 'analysis';
  data: {
    messageHistory: string[];
    timeSpent: number;
    engagement: number; // Numeric score, must be > 0.7 for summary generation
  };
}

// Section titles mapping
export const SECTION_TITLES: Record<string, string> = {
  keyInsights: 'Key Insights',
  personalObservations: 'Personal Observations',
  areasOfGrowth: 'Areas of Growth',
  challenges: 'Challenges',
  nextSteps: 'Next Steps',
};

// Character limits for sections
export const SECTION_LIMITS = {
  minLength: 50,
  maxLength: 500,
};